/** Split a merchant's charges into amount-consistent clusters.
 *
 * Some providers bill multiple distinct subscriptions under one identity
 * (e.g. "APPLE.COM/BILL" covering both iCloud and Apple Music). When those
 * amounts interleave, the merged charge history looks irregular and fails
 * cadence detection entirely. Clustering by amount recovers each one.
 */

import type { Transaction } from '../types';

/**
 * Tolerance is intentionally tight (unlike the ~25% amount-consistency
 * check used for a single subscription's price hikes): this only needs to
 * catch genuinely repeated prices, not a general spread. A loose tolerance
 * chains unrelated variable-amount charges (groceries, gas) into false
 * clusters via transitive sort-adjacency.
 */
function withinTolerance(amount: number, anchor: number): boolean {
  return Math.abs(amount - anchor) <= Math.max(0.5, anchor * 0.03);
}

export function clusterByAmount(txns: Transaction[]): Transaction[][] {
  const sorted = [...txns].sort((a, b) => a.amount - b.amount);
  const clusters: Transaction[][] = [];
  for (const t of sorted) {
    const current = clusters[clusters.length - 1];
    const anchor = current?.[0];
    if (current && anchor && withinTolerance(t.amount, anchor.amount)) {
      current.push(t);
    } else {
      clusters.push([t]);
    }
  }
  return clusters;
}
